"use client";

import { useEffect, useRef } from "react";
import { useSnapshot } from "valtio";

import { usePathname } from "@/src/hooks/usePathname";
import { isInterruptiveSurfaceRoute } from "@/src/hooks/onboarding/isOnboardingRoute";
import { useGlobalAppModalLimit } from "@/src/hooks/useGlobalAppModalLimit";
import { useShellRouter } from "@hooks/useShellAwareRouter";
import { handoffSessionStore } from "@hooks/onboarding/handoff/handoffSessionStore";
import { useBridge } from "@/src/lib/bridge";
import { Track } from "@/src/lib/tracking";
import { buildBriefReportHref } from "@screens/report/buildReportSequenceItems";
import { useReportSummary } from "@screens/brief/hooks/useReportSummary";
import { rootStore } from "@store/rootStore";
import { ModalStore } from "@store/modules/modal";
import { ROUTER_PATH } from "@config/router";
import {
  hasReportOpeningEntryToday,
  markReportOpeningEntered,
} from "./reportOpeningAutoshowStorage";

/** useGlobalAppModalLimit 弹窗标识（APP 多 WebView 协调备用；本期 APP 未接入自动弹） */
const REPORT_OPENING_AUTOSHOW_ID = "report-opening";

/**
 * 远程可配开关占位（fe-spec §3.8.1/§3.2）：本期默认开；
 * 远程配置接入后替换为远程读取（GAP：配置通道未定）。
 */
const REPORT_OPENING_AUTOSHOW_REMOTE_ENABLED = true;

/**
 * 迁移自旧 catchup 自动弹 hook 的 isPeerAutoModalOpen（fe-spec §3.8.1：
 * 旧 tracker welcome 弹窗态已随 catchup 下线、low credit 弹窗已下线，
 * 四弹窗互斥降为三弹窗内部互斥——
 * 新自动弹是路由 push 非弹窗，无新 modal-visible 态可读）。
 */
function isPeerAutoModalOpen(): boolean {
  // EDG-479/481：`turnOnNotificationModalVisible` 已随 web 版「开启通知」弹窗一起删除
  //（App 内 push 引导归原生，见 `features.nativePushPrimer`）⇒ 三弹窗互斥降为两弹窗。
  return !!(
    ModalStore.appVersionUpdateModalVisible || ModalStore.appRatingModalVisible
  );
}

function isReportRoute(pathname: string): boolean {
  return (
    pathname === ROUTER_PATH.BRIEF_REPORT ||
    pathname.startsWith(`${ROUTER_PATH.BRIEF_REPORT}/`)
  );
}

/**
 * 开场页自动弹（fe-spec §3.8.1/§3.2 · PRD-A S1.1）：当日首次打开 App 且
 * `unread_count >= 1` 时 `router.push('/brief/report?entry=auto')`。
 *
 * - 门槛：登录 + useReportSummary 的 unread_count ≥ 1（S2 query，取数失败/超时按 0 不弹）
 * - 额度：per owner × 用户本地自然日至多 1 次；同一自然日已被自动弹过或已主动进过
 *   只能从摘要条进入（PRD S1.1）——自动弹 push 与落到 /brief/report 均落定当日额度
 * - 互斥：peer 自动弹窗（appVersionUpdate / appRating）
 *   任一显示中不弹；关闭后随 effect 重试（attemptedRef 只在真正尝试后置位）
 * - 互斥：Handoff tour（onboarding 完成后的全屏手势引导，挂应用根部与路由无关）
 *   进行中让路——不 push、不置 attemptedRef、不消耗额度；引导结束后随
 *   handoffSession.active 翻转自动重试（两个「首次引导」互斥，防 coachmark
 *   全屏滞留叠在开场页上，验收实锤 2026-09）
 * - 远程开关：占位默认开（REPORT_OPENING_AUTOSHOW_REMOTE_ENABLED）
 * - fail-safe：额度写入失败不弹（Track.error；宁可少弹不可双弹，fe-spec §2.4）
 *
 * App 多 WebView：useGlobalAppModalLimit 协调 visible / broadcast / 远程关弹（机制迁移自
 * 旧 catchup 自动弹 hook，本期 APP 未接入自动弹，保留备用）。Web/H5 单页不校验
 * visible、claim 前即标记 attempted。
 */
export function useReportOpeningAutoshow() {
  const router = useShellRouter();
  const { isInApp } = useBridge();
  const pathname = usePathname();
  const { isLoggedIn } = useSnapshot(rootStore.login);
  const { ownerInfo } = useSnapshot(rootStore.user);
  const handoffSession = useSnapshot(handoffSessionStore);
  const ownerId = ownerInfo?.id;
  const { unreadCount } = useReportSummary();
  const { appVersionUpdateModalVisible, appRatingModalVisible } =
    useSnapshot(ModalStore);
  const attemptedRef = useRef(false);
  const { canShow, isSuppressed, tryOpen } = useGlobalAppModalLimit(
    REPORT_OPENING_AUTOSHOW_ID,
    { hideInAppChatModal: true },
  );
  const tryOpenRef = useRef(tryOpen);
  tryOpenRef.current = tryOpen;

  // 「已主动进过」也消耗当日额度（PRD S1.1）：落到 /brief/report 即落定
  // （auto push 后的路由变化同样经此幂等写；写入失败静默——下次进入重试落定）
  useEffect(() => {
    if (!ownerId || !isReportRoute(pathname)) {
      return;
    }
    markReportOpeningEntered(ownerId);
  }, [ownerId, pathname]);

  useEffect(() => {
    if (!REPORT_OPENING_AUTOSHOW_REMOTE_ENABLED) {
      return;
    }
    if (!isLoggedIn || unreadCount < 1) {
      return;
    }
    if (!ownerId) {
      return;
    }
    if (isInterruptiveSurfaceRoute(pathname)) {
      return;
    }
    if (attemptedRef.current) {
      return;
    }
    // Handoff tour 进行中让路：不 push、不置 attemptedRef、不消耗额度——
    // 引导结束后 handoffSession.active 翻转，本 effect 自动重试
    if (handoffSession.active) {
      return;
    }
    if (isPeerAutoModalOpen()) {
      return;
    }
    // 当日已弹过 / 已主动进过 → 不再自动弹（AC23；之后只能从摘要条进入）
    if (hasReportOpeningEntryToday(ownerId)) {
      attemptedRef.current = true;
      return;
    }

    const pushOpening = () => {
      // fail-safe：额度写入失败不弹（宁可少弹不可双弹）
      if (!markReportOpeningEntered(ownerId)) {
        Track.error(new Error("report opening autoshow quota write failed"), {
          tag: "useReportOpeningAutoshow",
          msg: "quota storage write failed, skip autoshow",
        });
        return;
      }
      router.push(buildBriefReportHref({ entry: "auto" }));
    };

    if (!isInApp) {
      attemptedRef.current = true;
      pushOpening();
      return;
    }

    if (isSuppressed) {
      attemptedRef.current = true;
      return;
    }
    if (!canShow) {
      return;
    }

    attemptedRef.current = true;
    tryOpenRef.current(pushOpening);
  }, [
    isLoggedIn,
    ownerId,
    unreadCount,
    pathname,
    isInApp,
    canShow,
    handoffSession.active,
    isSuppressed,
    router,
    appVersionUpdateModalVisible,
    appRatingModalVisible,
  ]);
}
